/*
 * IMPORTS
 */
import React from 'react' // Npm: React.js library.
import PropTypes from 'prop-types' // Npm: React.js library.
import _ from 'underscore' // Npm: Utility module.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { useQuery, useMutation } from '@apollo/client' // Npm: Apollo client.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Flex,
  Text,
  useBreakpointValue,
  useStyleConfig
} from '@chakra-ui/react' // Npm: A simple, modular and accessible component library for React.js.
import './index.css'


/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'


/*
 * GRAPHS
 */
import CustomerAccountConnectedQuery from './__query__/index.customerAccount.connected.query'
import SmppActiveInActiveMutation from './__mutation__/index.smpp.activeInActive.mutation'


function Index({ title, isConnect }) {
  // Const assignment.
  const _successFlags = ['READ_SUCCESSFUL']

  // Hook assignment.
  const [isCacheClearLoading, setIsCacheClearLoading] = React.useState(new Set())
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );
  const _appliedFilter = React.useMemo(
    () =>
      filterModel?.items?.some((i) => i.value != void 0 && i.value !== "")
        ? filterModel
        : void 0,
    [filterModel],
  );
  const _styles = useStyleConfig('Card')
  const [MutationSmppActiveInActive] = useMutation(SmppActiveInActiveMutation)
  const _QueryCustomerAccountConnected = useQuery(
    CustomerAccountConnectedQuery,
    {
      variables: {
        type: "WHOLESALE",
        isActive: Boolean(isConnect),
        take: paginationModel.pageSize,
        skip: paginationModel.page * paginationModel.pageSize,
        filter: _appliedFilter,
      },
      fetchPolicy: Object.React.App.fetchPolicy,
      pollInterval: Object.React.App.pollInterval,
    },
  );
  const _isCurrentViewMobile = useBreakpointValue({
    'base': 'false',
    'md': false,
    'lg': false,
    'xl': false,
    'sm': true,
    'xs': true
  })
  const _isFirstLoadCompleted = React.useRef(false)
  const _tableHeaderHeightRef = React.useRef(0)
  const _rowCountRef = React.useRef(0);

  React.useEffect(
    () => () => _setFilterDebounced.cancel(),
    [_setFilterDebounced],
  );

  // Data assignment.
  if (!_QueryCustomerAccountConnected.loading && 0 < _QueryCustomerAccountConnected.data?.CustomerAccountConnected?.length) _isFirstLoadCompleted.current = true
  if (0 === _QueryCustomerAccountConnected?.data?.CustomerAccountConnected?.length || (0 < _QueryCustomerAccountConnected?.data?.CustomerAccountConnected?.length && !_.every(_.pluck(_QueryCustomerAccountConnected?.data?.CustomerAccountConnected, 'status'), j => _successFlags.includes(j)))) _isFirstLoadCompleted.current = false

  // Cache clear handler
  const _handleCacheClear = React.useCallback(async (smppId) => {
    // If smpp is active then deactivate it.
    setIsCacheClearLoading(i => new Set([...i, smppId]))

    // Activate the smpp.
    const MutationSmppActiveInActiveResponse = await MutationSmppActiveInActive({ 'variables': { 'smppId': smppId, 'isActive': false, 'clearSmppBuild': true } })

    // If smpp is inactive then activate it.
    toast(MutationSmppActiveInActiveResponse?.data?.SmppActiveInActive?.message ?? 'Successfully cleared the smpp cache.')

    // Remove the smpp id from the loading array.
    setIsCacheClearLoading(i => {
      const _next = new Set(i)
      _next.delete(smppId)
      return _next
    })
  }, [MutationSmppActiveInActive])

  // IP Action Cell Component
  const _IPActionCell = React.useCallback(
    ({ row }) => (
      <Flex direction="column" justify="center" align="center" gap="0px">
        <Text
          color="rgba(43, 54, 116, 1)"
          fontWeight="700"
          fontSize="13px"
          mt="-8px"
          mb="-14px"
        >
          {row["Smpp.ip"]}
        </Text>
        <Button
          my="0px"
          height="10px"
          borderRadius={0}
          _hover={{ bg: "none" }}
          _active={{ bg: "none" }}
          bg="none"
          onClick={() => _handleCacheClear(row.smppId)}
          px="0"
          py="0"
          color="tomato"
        >
          {isCacheClearLoading.has(row.smppId) ? (
            <Text fontSize={12} fontWeight={700}>
              PLEASE WAIT
            </Text>
          ) : (
            <Text fontSize={12} fontWeight={700}>
              CLEAN CACHE
            </Text>
          )}
        </Button>
      </Flex>
    ),
    [_handleCacheClear, isCacheClearLoading],
  );

  // Status Cell Component
  const _StatusCell = React.useCallback(() => (
    <Button
      width='80.65px'
      height='18px'

      color={isConnect ? 'rgb(32, 32, 32)' : 'rgba(217, 11, 11, 1)'}
      fontSize='13px'
      background={isConnect ? 'rgba(46, 204, 113, 0.3)' : 'rgba(255, 242, 242, 1)'}>
      {isConnect ? '.Online' : '.Offline'}
    </Button>
  ), [isConnect])

  // Define columns
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        width: 60,
        disableColumnMenu: true,
        sortable: false,
        filterable: false,
        cellClassName: "cell-font",
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        headerAlign: "center",
        hide: true,
      },
      {
        field: "Smpp.ip",
        headerName: "IP | Action",
        align: "center",
        headerAlign: "center",
        width: 150,
        sortable: true,
        filterable: true,
        renderCell: _IPActionCell,
      },
      {
        field: "displayName",
        headerName: "Account",
        align: "center",
        headerAlign: "center",
        minWidth: 150,
        flex: 1,
        sortable: true,
        filterable: true,
        cellClassName: "cell-font",
      },
      {
        field: "status",
        headerName: "Status",
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 100,
        sortable: true,
        filterable: false,
        renderCell: _StatusCell,
      },
      {
        field: "mode",
        headerName: "Mode",
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 80,
        sortable: true,
        filterable: false,
        cellClassName: "cell-font",
      },
    ],
    [_IPActionCell, _StatusCell],
  );

  // Define rows
  const rows = React.useMemo(() => {
    return _.compact(_QueryCustomerAccountConnected?.data?.CustomerAccountConnected?.map((item, index) => {
      if (_.isEmpty(item?.Smpp?.id) || item.status !== 'READ_SUCCESSFUL') return null

      return {
        id:
          item?.Smpp?.id ??
          `customer-account-connected-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
        sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
        smppId: item?.Smpp?.id,
        "Smpp.ip": item?.Smpp?.ip,
        displayName: item?.displayName,
        mode: item?.Smpp?.mode ?? "-",
        status: item?.status,
      };
    }))
  }, [_QueryCustomerAccountConnected?.data?.CustomerAccountConnected, paginationModel.page, paginationModel.pageSize])

  const _QueryCustomerAccountConnectedRowCount = _.first(
    _QueryCustomerAccountConnected?.data?.CustomerAccountConnected,
  )?._totalCount;
  const totalCount = React.useMemo(() => {
    if (
      undefined !== _QueryCustomerAccountConnectedRowCount &&
      null !== _QueryCustomerAccountConnectedRowCount
    )
      _rowCountRef.current = _QueryCustomerAccountConnectedRowCount;

    return _rowCountRef.current;
  }, [_QueryCustomerAccountConnectedRowCount]);
  const _HandleFilterModelChange = React.useCallback(
    (__filterModel) => {
      setPaginationModel((current) => ({ ...current, page: 0 }));
      _setFilterDebounced(__filterModel);
    },
    [_setFilterDebounced],
  );

  // Return component.
  return (
    <Box
      __css={_styles}
      justifyContent="center"
      align="center"
      display="flex"
      direction="column"
      height="100%"
      w="100%"
      p={_isCurrentViewMobile ? "12px" : "22px"}
      gap={_isCurrentViewMobile ? "12px" : "22px"}
      fontSize={["clamp(12px, 1.5vw, 15px)"]}
      boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.17)"
    >
      <Flex justify="space-between" alignItems="center" width="100%">
        <Text
          color="rgba(43,54,116,1)"
          fontWeight="700"
          fontSize={{ base: "sm", md: "md" }}
          textAlign="left"
        >
          {title ?? "NOT CONNECTED CUSTOMER ACCOUNTS"}
        </Text>
      </Flex>
      <Box width="100%" height="450px">
        <DataGrid
          rows={rows}
          columns={columns}
          rowHeight={45}
          paginationMode="server"
          filterMode="server"
          rowCount={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          filterModel={filterModel}
          onFilterModelChange={_HandleFilterModelChange}
          pageSizeOptions={[10, 50, 100]}
          slots={{
            loadingOverlay: () => (
              <TableSpinner
                isLoading={true}
                isEmpty={false}
                chopHeightFromHundredPercentage={
                  _tableHeaderHeightRef?.current?.clientHeight
                }
              />
            ),
            noResultsOverlay: () => (
              <TableSpinner
                isLoading={false}
                isEmpty={true}
                chopHeightFromHundredPercentage={
                  _tableHeaderHeightRef?.current?.clientHeight
                }
              />
            ),
            noRowsOverlay: () => (
              <TableSpinner
                isLoading={false}
                isEmpty={true}
                chopHeightFromHundredPercentage={
                  _tableHeaderHeightRef?.current?.clientHeight
                }
              />
            ),
          }}
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) =>
            setColumnVisibilityModel(newModel)
          }
          loading={_QueryCustomerAccountConnected?.loading}
        />
      </Box>
    </Box>
  );
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'isConnect': PropTypes.bool,
  'title': PropTypes.string
}


/*
 * EXPORTS
 */
export default Index
